import * as React from 'react';
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from 'react-icons/md';
import {
  Button,
  DateTextBox,
  FlexLayout,
  FormatNumberTextBox,
  FormatTextBox,
  GridLayout,
  LayoutTitle,
  ModalView,
  OptionBox,
  SearchLayout,
  SubHeader,
  TabView,
  TextBox,
} from '../../../../components';
import {
  BankStatement,
  BankStatementTabDataStateNames,
} from './BankStatement';
import {
  Global,
  TemplateProps,
} from '../../../../constants';
import { Date8 } from '../../../../utils/time';
import { SearchBinding } from '../../../../models/common';
import { Sum } from '../../../../utils/array';
import { Format } from '../../../../utils/string';

const acccds = [
  { value: '1011', remark: '현금' },
  { value: '1012', remark: '현금(외화)' },
  { value: '1013', remark: '당좌예금' },
  { value: '1015', remark: '보통예금' },
  { value: '1016', remark: '보통예금(외화)' },
  { value: '1031', remark: '정기예금' },
  { value: '1032', remark: '정기적금' },
  { value: '1611', remark: '정기예금' },
  { value: '1684', remark: '퇴직보험예치금' },
  { value: '2112', remark: '단기차입금(일반)' },
];

const signflags = [
  { value: '0', remark: '미연동' },
  { value: '1', remark: '연동계좌' },
  { value: '2', remark: '실패' },
  { value: '3', remark: '연동대기' },
];

/**
 * 화면
 * @window w_tb_ca642w_10
 * @category 입출금현황
 */
export const BankStatementTemplate: React.FC<TemplateProps<BankStatement>> = ({
  scope,
}) => {
  // @ts-ignore
  const setData = (data: any) => scope?.setState(data);

  return (
    <FlexLayout isVertical={true}>
      <FlexLayout size={Global.LAYOUT_SEARCH_HEIGHT_1}>
        <SearchLayout>
          <FlexLayout size={200}>
            <span>기간</span>
            <DateTextBox
              value={scope.state.stdate}
              textAlign="center"
              format="####-##-##"
              onChange={(value) => setData({ stdate: value })}
              onEnter={() => scope.onRetrieveEvent()}
              head={(<button
                onClick={() => {
                  const date = new Date(Date8.withDash(scope.state.stdate));
                  date.setDate(date.getDate() - 1);
                  scope.setState({ stdate: Date8.make(date) },
                    () => scope.onRetrieveEvent());
                }}
              >
                <MdKeyboardArrowLeft size={24}/>
              </button>)}
              trail={(<button
                onClick={() => {
                  const date = new Date(Date8.withDash(scope.state.stdate));
                  date.setDate(date.getDate() + 1);
                  scope.setState({ stdate: Date8.make(date) },
                    () => scope.onRetrieveEvent());
                }}
              >
                <MdKeyboardArrowRight size={24}/>
              </button>)}
              isDisabledTrackingStateChange={true}
            elField="stdate"
            />
          </FlexLayout>

          <FlexLayout size={180}>
            <span style={{ marginLeft: 8 }}>~</span>
            <DateTextBox
              value={scope.state.enddate}
              textAlign="center"
              format="####-##-##"
              onChange={(value) => setData({ enddate: value })}
              onEnter={() => scope.onRetrieveEvent()}
              head={(<button
                onClick={() => {
                  const date = new Date(Date8.withDash(scope.state.enddate));
                  date.setDate(date.getDate() - 1);
                  scope.setState({ enddate: Date8.make(date) },
                    () => scope.onRetrieveEvent());
                }}
              >
                <MdKeyboardArrowLeft size={24}/>
              </button>)}
              trail={(<button
                onClick={() => {
                  const date = new Date(Date8.withDash(scope.state.enddate));
                  date.setDate(date.getDate() + 1);
                  scope.setState({ enddate: Date8.make(date) },
                    () => scope.onRetrieveEvent());
                }}
              >
                <MdKeyboardArrowRight size={24}/>
              </button>)}
              isDisabledTrackingStateChange={true}
            elField="enddate"
            />
          </FlexLayout>

          <FlexLayout size={400}>
            <span style={{ color: 'var(--color-red)' }}>계좌번호</span>
            <TextBox
              weight={1.5}
              value={scope.state.bankcds}
              textAlign="center"
              onChange={(value) => setData({ bankcds: value })}
              isDisabledTrackingStateChange={true}
              bindSearchModal={new SearchBinding(
                scope.props.modalStore,
                'TB_AA040',
                {},
                true,
                (item) => {
                  setData({
                    bankcds: item.cd,
                    banknms: item.uv_arg1,
                    accnums: item.uv_arg3,
                  });
                  scope.onRetrieveEvent();
                },
              )}
            elField="bankcds"
            />
            <TextBox
              value={scope.state.banknms}
              weight={1.5}
              readonly={true}
            elField="banknms"
            />
            <TextBox
              value={scope.state.accnums}
              weight={1.7}
              readonly={true}
            elField="accnums"
            />
          </FlexLayout>

          <FlexLayout/>
          <Button onClick={() => scope.textModal(true)}>
            입출금비교
          </Button>

          <Button onClick={() => scope.shortModal(true)}>
            월별단기차입금
          </Button>
        </SearchLayout>
      </FlexLayout>

      <FlexLayout>
        <FlexLayout>
          <GridLayout
            ref={scope.grid}
            header={[
              {
                id: 'banknm',
                text: '은행명',
                width: 13,
                trail: () => (
                  <FlexLayout
                    justify="center"
                    align="center"
                  >
                    합 계
                  </FlexLayout>
                ),
                render: (x) => (
                  <FlexLayout
                    justify="left"
                    align="center"
                  >
                    {x.banknm}
                  </FlexLayout>
                ),
              },
              {
                id: 'accname',
                text: '계좌명칭',
                width: 14,
                render: (x) => (
                  <FlexLayout
                    justify="left"
                    align="center"
                  >
                    {x.accname}
                  </FlexLayout>
                ),
              },
              {
                id: 'accnum',
                text: '계좌번호',
                width: 14,
                render: (x) => (
                  <FlexLayout
                    justify="left"
                    align="center"
                  >
                    {x.accnum}
                  </FlexLayout>
                ),
              },
              {
                id: 'acccd',
                text: '계좌계정',
                width: 9,
                render: (x) => (
                  <FlexLayout
                    justify="center"
                    align="center"
                    style={{ color: x.acccd === '2112' ? 'var(--color-red)' : 'var(--color-blue)' }}>
                    {acccds.filter((y) => y.value === x.acccd)[0]?.remark}
                  </FlexLayout>
                ),
              },
              {
                id: 'beamt',
                text: '전일잔액',
                width: 9,
                trail: () => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {scope.state.beamt_tot}
                  </FlexLayout>
                ),
                render: (x) => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {Format.number(x.beamt)}
                  </FlexLayout>
                ),
              },
              {
                id: 'misamt',
                text: '입금금액',
                width: 9,
                sum: (x, data) => {
                  const sumMisamt = Sum.byGroup(data, 'misamt', x, []);
                  return (
                    <FlexLayout
                      justify="right"
                      align="center"
                    >
                      {Format.number(sumMisamt)}
                    </FlexLayout>
                  );
                },
                trail: () => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {scope.state.misamt_tot}
                  </FlexLayout>
                ),
                render: (x) => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {Format.number(x.misamt)}
                  </FlexLayout>
                ),
              },
              {
                id: 'mijamt',
                text: '지급금액',
                width: 9,
                trail: () => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {scope.state.mijamt_tot}
                  </FlexLayout>
                ),
                render: (x) => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {Format.number(x.mijamt)}
                  </FlexLayout>
                ),
              },
              {
                id: 'resuamt',
                text: '잔액',
                width: 9,
                trail: () => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {scope.state.resuamt_tot}
                  </FlexLayout>
                ),
                render: (x) => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {Format.number(x.resuamt)}
                  </FlexLayout>
                ),
              },
              {
                id: 'signflag',
                text: '연동',
                width: 5,
                render: (x) => (
                  <FlexLayout
                    align='center'
                    justify='center'
                    style={{
                      // eslint-disable-next-line no-nested-ternary
                      color: x.signflag === '2'
                        ? 'var(--color-red)'
                        : x.signflag === '1' ? 'var(--color-blue)' : '',
                    }}>
                    {signflags.filter((y) => y.value === x.signflag)[0]?.remark}
                  </FlexLayout>
                ),
              },
              {
                id: 'bank_resuamt',
                text: '은행잔액',
                width: 9,
                render: (x) => (
                  <FormatNumberTextBox
                    char={','}
                    charAt={3}
                    textAlign="right"
                    value={x.bank_resuamt}
                  />
                ),
                trail: () => (
                  <FlexLayout
                    justify="right"
                    align="center"
                  >
                    {scope.state.bank_resuamt_tot}
                  </FlexLayout>
                ),
              },
              {
                id: 'compare',
                text: '비교',
                width: 5,
                render: (x) => (
                  <FlexLayout
                    align='center'
                    justify='center'
                    style={{
                      color: x.compare === '상이' ? 'var(--color-red)' : 'var(--color-blue)',
                    }}
                  >
                    {x.signflag === '1' ? x.compare : ''}
                  </FlexLayout>
                ),
              },
            ]}
             data={scope.state.data}
             infinityHandler={scope.infinity}
             rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
             onRowClick={(item, index) => scope.onRowFocusEvent(item, index)}
             onRowDoubleClick={() => scope.textModal(true)}
          elGrid="data"
          />
        </FlexLayout>
      </FlexLayout>

      <FlexLayout size={Global.LAYOUT_SEARCH_HEIGHT_1}>
        <TabView
          tabs={scope.tabs}
          focused={scope.state.focusedTab}
          onChange={(tab) => scope.onTabChange(tab)}
        />
      </FlexLayout>
      <FlexLayout weight={1.5}>
        <GridLayout
          ref={scope.grid2}
          scope={scope}
          header={scope.tabHeaders[scope.tabIndex]}
          rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
          infinityHandler={scope.infinity2}
          // @ts-ignore
          data={scope.state[BankStatementTabDataStateNames[scope.tabIndex]] || [] }
        />
      </FlexLayout>

      <ModalView
        isVisible={scope.state.textDetailModal}
        onClose={() => scope.textModal(false)}
        width={1800}
      >
        <FlexLayout
          isVertical={true}
          style={{ padding: 8 }}
        >
          <FlexLayout size={Global.LAYOUT_SEARCH_HEIGHT_1} >
            <SearchLayout>
              <FlexLayout size={200}>
                <span>기간</span>
                <DateTextBox
                  value={scope.state.stdate}
                  textAlign="center"
                  format="####-##-##"
                  onChange={(value) => setData({ stdate: value })}
                  onEnter={() => scope.onRetrieveEvent()}
                  head={(<button
                    onClick={() => {
                      const date = new Date(Date8.withDash(scope.state.stdate));
                      date.setDate(date.getDate() - 1);
                      scope.setState({ stdate: Date8.make(date) },
                        () => scope.onRetrieveEvent());
                    }}
                  >
                    <MdKeyboardArrowLeft size={24}/>
                  </button>)}
                  trail={(<button
                    onClick={() => {
                      const date = new Date(Date8.withDash(scope.state.stdate));
                      date.setDate(date.getDate() + 1);
                      scope.setState({ stdate: Date8.make(date) },
                        () => scope.onRetrieveEvent());
                    }}
                  >
                    <MdKeyboardArrowRight size={24}/>
                  </button>)}
                  isDisabledTrackingStateChange={true}
                />
              </FlexLayout>

              <FlexLayout size={180}>
                <span style={{ marginLeft: 8 }}>~</span>
                <DateTextBox
                  value={scope.state.enddate}
                  textAlign="center"
                  format="####-##-##"
                  onChange={(value) => setData({ enddate: value })}
                  onEnter={() => scope.onRetrieveEvent()}
                  head={(<button
                    onClick={() => {
                      const date = new Date(Date8.withDash(scope.state.enddate));
                      date.setDate(date.getDate() - 1);
                      scope.setState({ enddate: Date8.make(date) },
                        () => scope.onRetrieveEvent());
                    }}
                  >
                    <MdKeyboardArrowLeft size={24}/>
                  </button>)}
                  trail={(<button
                    onClick={() => {
                      const date = new Date(Date8.withDash(scope.state.enddate));
                      date.setDate(date.getDate() + 1);
                      scope.setState({ enddate: Date8.make(date) },
                        () => scope.onRetrieveEvent());
                    }}
                  >
                    <MdKeyboardArrowRight size={24}/>
                  </button>)}
                  isDisabledTrackingStateChange={true}
                />
              </FlexLayout>

              <Button onClick={() => scope.textModal(true)}>
                조회
              </Button>
            </SearchLayout>

            <FlexLayout isVertical={true} weight={0.4}>
              <FlexLayout>
                입금액:
                <FlexLayout
                  style={{
                    color: scope.state.popupData?.rcv === '같음' ? 'var(--color-blue)' : 'var(--color-red)',
                  }}
                >
                  {!scope.state.popupData?.rcvamt ? '동일' : scope.state.popupData?.rcv}
                </FlexLayout>
                지급액:
                <FlexLayout
                  style={{
                    color: scope.state.popupData?.snd === '같음' ? 'var(--color-blue)' : 'var(--color-red)',
                  }}
                >
                  {!scope.state.popupData?.sndamt ? '동일' : scope.state.popupData?.snd}
                </FlexLayout>
                잔액:
                <FlexLayout
                  style={{
                    color: scope.state.popupData?.resu === '같음' ? 'var(--color-blue)' : 'var(--color-red)',
                  }}
                >
                  {!scope.state.popupData?.resuamt ? '동일' : scope.state.popupData?.resu}
                </FlexLayout>
              </FlexLayout>

              <FlexLayout>
                <FlexLayout>
                  {scope.state.popupData?.rcvamt
                    ? <FlexLayout>
                      차액:
                      <FlexLayout style={{ color: 'var(--color-red)' }}>
                        {scope.state.popupData?.rcvamt}
                      </FlexLayout>
                    </FlexLayout>
                    : ''}
                </FlexLayout>

                <FlexLayout>
                  {scope.state.popupData?.sndamt
                    ? <FlexLayout>
                      차액:
                      <FlexLayout style={{ color: 'var(--color-red)' }}>
                        {scope.state.popupData?.sndamt}
                      </FlexLayout>
                    </FlexLayout>
                    : ''}
                </FlexLayout>

                <FlexLayout>
                  {scope.state.popupData?.resuamt
                    ? <FlexLayout>
                      차액:
                      <FlexLayout style={{ color: 'var(--color-red)' }}>
                        {scope.state.popupData?.resuamt}
                      </FlexLayout>
                    </FlexLayout>
                    : ''}
                </FlexLayout>
              </FlexLayout>
            </FlexLayout>
          </FlexLayout>
        </FlexLayout>

        <FlexLayout weight={12} style={{ padding: 8 }}>
          <FlexLayout isVertical={true}>
            <FlexLayout size={20}>
              <LayoutTitle size={150}>은행내역</LayoutTitle>
            </FlexLayout>

            <FlexLayout>
              <GridLayout
                header={[
                  {
                    id: 'misdate',
                    text: '발생일자',
                    width: 16,
                    trail: () => (
                      <FlexLayout
                        justify="center"
                        align="center"
                      >
                        합 계
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="center"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Date8.withDash(x.misdate)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'cltcd',
                    text: '시간',
                    width: 16,
                    render: (x) => (
                      <FormatTextBox
                        textAlign="center"
                        format="##:##"
                        color={ x.flag === '1' ? 'var(--color-red)' : '' }
                        value={x.cltcd}
                      />
                    ),
                  },
                  {
                    id: 'cltnm',
                    text: '지급처명',
                    width: 17,
                    render: (x) => (
                      <FlexLayout
                        justify="left"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {x.cltnm}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'misamt',
                    text: '입금액',
                    width: 17,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.pmisamt_tot}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.misamt)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'mijamt',
                    text: '지급액',
                    width: 17,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.pmijamt_tot}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.mijamt)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'resuamt',
                    text: '잔액',
                    width: 17,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.presuamt_tot}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.resuamt)}
                      </FlexLayout>
                    ),
                  },
                ]}
                data={scope.state.popupbank}
                rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
              elGrid="popupbank"
              />
            </FlexLayout>

            <SubHeader>
            </SubHeader>
          </FlexLayout>

          <FlexLayout isVertical={true}>
            <FlexLayout size={20}>
              <LayoutTitle size={150}>ERP내역</LayoutTitle>
            </FlexLayout>

            <FlexLayout>
              <GridLayout
                header={[
                  {
                    id: 'misdate',
                    text: '발생일자',
                    width: 12,
                    trail: () => (
                      <FlexLayout
                        justify="center"
                        align="center"
                      >
                        합 계
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="center"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Date8.withDash(x.misdate)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'misnum',
                    text: '번호',
                    width: 10,
                    render: (x) => (
                      <FlexLayout
                        justify="center"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {x.misnum}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'remark',
                    text: '적요',
                    width: 21,
                    render: (x) => (
                      <FlexLayout
                        justify="left"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {x.remark}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'cltnm',
                    text: '지급처명',
                    width: 20,
                    render: (x) => (
                      <FlexLayout
                        justify="left"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {x.cltnm}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'misamt',
                    text: '입금액',
                    width: 12,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.pmisamt_tot2}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.misamt)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'mijamt',
                    text: '지급액',
                    width: 12,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.pmijamt_tot2}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.mijamt)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'resuamt',
                    text: '잔액',
                    width: 13,
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        align="center"
                      >
                        {scope.state.presuamt_tot2}
                      </FlexLayout>
                    ),
                    render: (x) => (
                      <FlexLayout
                        justify="right"
                        align="center"
                        style={{ color: x.flag === '1' ? 'var(--color-red)' : '' }}
                      >
                        {Format.number(x.resuamt)}
                      </FlexLayout>
                    ),
                  },
                ]}
                data={scope.state.popupdetail}
                rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
              elGrid="popupdetail"
              />
            </FlexLayout>

            <SubHeader>
              <FlexLayout justify="end">
                <Button onClick={() => scope.textModal(false)}>닫기</Button>
              </FlexLayout>
            </SubHeader>
          </FlexLayout>
        </FlexLayout>
      </ModalView>

      <ModalView
        isVisible={scope.state.shortModal}
        onClose={() => scope.SS({ shortModal: false })}
        width={1800}
      >
        <FlexLayout
          isVertical={true}
          style={{ padding: 8 }}
        >
          <FlexLayout size={Global.LAYOUT_SEARCH_HEIGHT_1} >
            <SearchLayout>
              <FlexLayout size={200}>
                <span>년도</span>
                <DateTextBox
                  value={scope.state.shortModalYear}
                  textAlign="center"
                  format="####"
                  onChange={(value) => scope.setState({ shortModalYear: value })}
                  head={(
                    <button
                      onClick={() => {
                        const date = new Date(`${scope.state.shortModalYear}-01-01`);
                        date.setFullYear(date.getFullYear() - 1);
                        scope.setState({ shortModalYear: date.getFullYear().toString() },
                          () => scope.shortModalRetrieve());
                      }}
                    >
                      <MdKeyboardArrowLeft size={24}/>
                    </button>
                  )}
                  trail={(
                    <button
                      onClick={() => {
                        const date = new Date(`${scope.state.shortModalYear}-01-01`);
                        date.setFullYear(date.getFullYear() + 1);
                        scope.setState({ shortModalYear: date.getFullYear().toString() },
                          () => scope.shortModalRetrieve());
                      }}
                    >
                      <MdKeyboardArrowRight size={24}/>
                    </button>
                  )}
                  isDisabledTrackingStateChange={true}
                />
              </FlexLayout>
            </SearchLayout>
          </FlexLayout>
        </FlexLayout>

        <FlexLayout weight={12} style={{ padding: 8 }}>
          <FlexLayout isVertical={true}>
            <FlexLayout>
              <GridLayout
                ref={scope.shortModalGrid}
                header={[
                  {
                    id: 'banknm2',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout>은행명</FlexLayout>
                        <FlexLayout>계좌번호</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 140,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout justify="left" align="center">{x.banknm}</FlexLayout>
                        <FlexLayout justify="left" align="center">{x.accnum}</FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout>
                        합 계
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'accname',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout>계좌명</FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    width: 140,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout justify="left" align="center">{x.accname}</FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                  },

                  {
                    id: 'bamt',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout>한도(대출)금액</FlexLayout>
                        <FlexLayout>최초발생일</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.bamt_st) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.bamt_st)}
                        </FlexLayout>
                        <FlexLayout
                          justify="left"
                          align="center">
                          {Date8.withDash(x.snddate_st)}
                        </FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.bamt_st_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.bamt_st_tot)}
                      </FlexLayout>
                    ),
                  },

                  {
                    id: 'sndamttot',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-blue)' }}>합계</FlexLayout>
                        <FlexLayout>대출남은금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamttot) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamttot)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt_tot_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt_tot_tot)}
                      </FlexLayout>
                    ),
                  },

                  {
                    id: 'beamt',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'brown' }}>전년도이월</FlexLayout>
                        <FlexLayout style={{ color: 'brown' }}>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={{ color: 'brown' }}>
                          {Format.number(x.beamt)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={{ color: 'var(--color-violet)' }}
                      >
                        {Format.number(scope.state.bamt_st_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt01',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>01월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt01) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt01)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt01_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt01_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt02',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>02월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt02) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt02)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt02_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt02_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt03',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>03월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt03) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt03)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt03_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt03_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt04',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>04월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt04) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt04)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt04_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt04_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt05',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>05월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt05) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt05)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt05_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt05_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt06',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>06월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt06) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt06)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt06_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt06_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt07',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>07월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt07) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt07)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt07_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt07_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt08',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>08월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt08) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt08)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt08_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt08_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt09',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>09월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt09) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt09)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt09_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt09_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt10',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>10월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt10) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt10)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt10_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt10_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt11',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>11월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt11) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt11)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt11_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt11_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamt12',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-green)' }}>12월</FlexLayout>
                        <FlexLayout>대출/상환금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamt12) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamt12)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt12_tot.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt12_tot)}
                      </FlexLayout>
                    ),
                  },
                  {
                    id: 'sndamttot_1',
                    text: (
                      <FlexLayout isVertical={true}>
                        <FlexLayout style={{ color: 'var(--color-blue)' }}>합계</FlexLayout>
                        <FlexLayout>대출/남은금액</FlexLayout>
                      </FlexLayout>
                    ),
                    width: 120,
                    render: (x) => (
                      <FlexLayout isVertical={true}>
                        <FlexLayout
                          justify="right"
                          align="center"
                          style={Number(x.sndamttot_1) >= 0 ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}>
                          {Format.number(x.sndamttot_1)}
                        </FlexLayout>
                        <FlexLayout></FlexLayout>
                      </FlexLayout>
                    ),
                    trail: () => (
                      <FlexLayout
                        justify="right"
                        style={!scope.state.sndamt_tot_tot_1.startsWith('-') ? { color: 'var(--color-blue)' } : { color: 'var(--color-red)' }}
                      >
                        {Format.number(scope.state.sndamt_tot_tot_1)}
                      </FlexLayout>
                    ),
                  },
                ]}
                data={scope.state.shortModalData}
                rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
              elGrid="shortModalData"
              />
            </FlexLayout>

            <SubHeader>
              <FlexLayout justify="end">
                <Button onClick={() => scope.shortModalExcel()}>EXCEL DOWNLOAD</Button>
                <Button onClick={() => scope.shortModal(false)}>닫기</Button>
              </FlexLayout>
            </SubHeader>
          </FlexLayout>
        </FlexLayout>
      </ModalView>

      <ModalView
        width={580}
        height={145}
        isVisible={scope.state.excelGubunModal}
        onClose={() => setData({ excelGubun: false })}
        zIndex={-1}
      >
        <FlexLayout
          isVertical={true}
          style={{
            padding: 8,
          }}
        >
          <LayoutTitle>선택</LayoutTitle>
          <FlexLayout>
            <OptionBox
              value={scope.state.excelGubun}
              data={[
                { value: '1', remark: '계좌별' },
                { value: '2', remark: '상세내역' },
              ]}
              onChange={(item) => scope.SS({ excelGubun: item.value.toString() })}
            />
          </FlexLayout>

          <SubHeader>
            <FlexLayout justify="end">
              <Button onClick={() => scope.onExcelGoing()}>확인</Button>
              <Button onClick={() => scope.SS({ excelGubunModal: false, excelGubun: '1' })}>취소</Button>
            </FlexLayout>
          </SubHeader>
        </FlexLayout>
      </ModalView>
    </FlexLayout>
  );
};
